import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  KioskActivationStatus,
  KioskActivationTokenResult,
  KioskListFilters,
  KioskListResult,
  KioskRecord,
  KioskRegisterInput,
} from '../types/kiosk';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function requiredId(value: unknown, label: string): number {
  const parsed = numberOrNull(value);
  if (parsed === null || parsed <= 0) {
    throw Object.assign(new Error(`${label} is missing`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return parsed;
}

function requiredText(value: unknown, label: string): string {
  const text = textOrNull(value);
  if (!text) {
    throw Object.assign(new Error(`${label} is required`), {
      code: 'VALIDATION_ERROR',
    });
  }
  return text;
}

function emptyToNull(value: string | null | undefined): string | null {
  if (value === undefined || value === null) return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

export function normalizeKioskRecord(row: ObjectValue): KioskRecord {
  return {
    kioskId: requiredId(row.kioskId, 'kioskId'),
    kioskUuid: textOrNull(row.kioskUuid),
    organizationId: requiredId(row.organizationId, 'organizationId'),
    locationId: numberOrNull(row.locationId),
    kioskName: textOrNull(row.kioskName) ?? 'Kiosk',
    deviceId: textOrNull(row.deviceId),
    deviceName: textOrNull(row.deviceName),
    deviceIdentifier: textOrNull(row.deviceIdentifier),
    status: String(row.status || '').toUpperCase(),
    activatedAt: textOrNull(row.activatedAt),
    lastSeenAt: textOrNull(row.lastSeenAt),
    activationTokenExpiresAt: textOrNull(row.activationTokenExpiresAt),
    createdAt: textOrNull(row.createdAt),
    updatedAt: textOrNull(row.updatedAt),
    rowVersion: numberOrNull(row.rowVersion),
  };
}

function normalizeActivationStatus(data: ObjectValue): KioskActivationStatus {
  const status = String(data.status || '').toUpperCase();
  return {
    kioskId: requiredId(data.kioskId, 'kioskId'),
    organizationId: requiredId(data.organizationId, 'organizationId'),
    status,
    activatedAt: textOrNull(data.activatedAt),
    activationTokenExpiresAt: textOrNull(data.activationTokenExpiresAt),
    isActivated:
      data.isActivated === true ||
      data.isActivated === 1 ||
      status === 'ACTIVE',
    isPending:
      data.isPending === true ||
      data.isPending === 1 ||
      status === 'PENDING',
  };
}

export async function listKiosks(input: {
  page: number;
  pageSize: number;
  filters?: KioskListFilters;
}): Promise<KioskListResult> {
  const response = await apiClient.get<
    Envelope<{
      items?: ObjectValue[];
      pagination?: {
        page?: unknown;
        pageSize?: unknown;
        totalRecords?: unknown;
        totalPages?: unknown;
      };
    }>
  >(API_ENDPOINTS.KIOSKS.LIST, {
    params: {
      page: input.page,
      pageSize: input.pageSize,
      ...(input.filters?.status
        ? { status: String(input.filters.status).toUpperCase() }
        : {}),
    },
  });
  const data = response.data.data ?? {};
  const pagination = data.pagination ?? {};
  const items = (data.items ?? [])
    .map(row => {
      try {
        return normalizeKioskRecord(row);
      } catch {
        return null;
      }
    })
    .filter((item): item is KioskRecord => item != null);

  const page = numberOrNull(pagination.page) ?? input.page;
  const pageSize = numberOrNull(pagination.pageSize) ?? input.pageSize;
  const totalItems = numberOrNull(pagination.totalRecords) ?? items.length;
  const totalPages =
    numberOrNull(pagination.totalPages) ??
    Math.ceil(totalItems / pageSize) ??
    0;

  return { items, page, pageSize, totalItems, totalPages };
}

export async function getKiosk(kioskId: number): Promise<KioskRecord> {
  const response = await apiClient.get<Envelope<ObjectValue>>(
    API_ENDPOINTS.KIOSKS.DETAILS(kioskId),
  );
  return normalizeKioskRecord(response.data.data ?? {});
}

export async function registerKiosk(
  input: KioskRegisterInput,
): Promise<KioskRecord> {
  const body: Record<string, unknown> = {
    kioskName: requiredText(input.kioskName, 'kioskName'),
  };
  const deviceName = emptyToNull(input.deviceName);
  const deviceIdentifier = emptyToNull(input.deviceIdentifier);
  if (deviceName) body.deviceName = deviceName;
  if (deviceIdentifier) body.deviceIdentifier = deviceIdentifier;
  if (input.locationId != null && input.locationId > 0) {
    body.locationId = input.locationId;
  }

  const response = await apiClient.post<Envelope<ObjectValue>>(
    API_ENDPOINTS.KIOSKS.CREATE,
    body,
  );
  return normalizeKioskRecord(response.data.data ?? {});
}

export async function generateKioskActivationToken(
  kioskId: number,
): Promise<KioskActivationTokenResult> {
  const response = await apiClient.post<Envelope<ObjectValue>>(
    API_ENDPOINTS.KIOSKS.ACTIVATION_TOKEN(kioskId),
    {},
  );
  const data = response.data.data ?? {};
  return {
    kiosk: normalizeKioskRecord((data.kiosk as ObjectValue) || {}),
    activationToken: requiredText(data.activationToken, 'activationToken'),
    expiresAt: requiredText(data.expiresAt, 'expiresAt'),
  };
}

export async function deactivateKiosk(kioskId: number): Promise<KioskRecord> {
  const response = await apiClient.post<Envelope<ObjectValue>>(
    API_ENDPOINTS.KIOSKS.DEACTIVATE(kioskId),
    {},
  );
  return normalizeKioskRecord(response.data.data ?? {});
}

export async function getKioskActivationStatus(
  kioskId: number,
): Promise<KioskActivationStatus> {
  const response = await apiClient.get<Envelope<ObjectValue>>(
    API_ENDPOINTS.KIOSKS.KIOSK_ACTIVATION_STATUS(kioskId),
  );
  return normalizeActivationStatus(response.data.data ?? {});
}

export function kioskStatusLabel(status: string | null | undefined): string {
  const normalized = String(status || '').toUpperCase();
  if (normalized === 'PENDING') return 'PENDING';
  if (normalized === 'ACTIVE') return 'ACTIVE';
  if (normalized === 'INACTIVE') return 'INACTIVE';
  return normalized || 'UNKNOWN';
}
